define([
	"dojo/_base/declare",
	"c8-numeraljs",
	"csi/heading/_base",
	"csi/heading/Attribute",
	"csi/locale",
	"csi/middleware/inline/InlinePopupService",
	"csi/types",
	"csi/widget/NumberTextBox",
	"csi/widget/PercentageTextBox",
	"csi/widget/ValidationTextBox",
	"csi/xml",
	"src/c8-platform/type/uom",
	"./ValidateError"
], function(declare, {bigDiv, bigTimes}, csiHeading, Attribute, locale, {CONTROL_TYPE_TEXT_FIELD}, types, NumberTextBox, PercentageTextBox, ValidationTextBox, xml, {convertUOMValue, getUOMConversionString, isValueUOMAttributeOnNode}, ValidateError) {

	return declare(Attribute, {
		defaultValue: 0,
		queryOps: csiHeading.QUERY_OPS[xml.ATTR_DOUBLE],
		tagAction: "ActionNumber",

		// Number: conversion factor to be used for all display (attr value * _nFactor) and editing (entered value / _nFactor)
		_nFactor: 1.0,

		formatValue: function(/*Number*/value, /*String?*/fmt) {
			// summary: convert value to displayed value based on format - relies on factor being set in toValue
			if (value == null) {
				return "";
			}

			return types.getNumberRead(bigTimes(value, this._nFactor), fmt);
		},

		/**
		 * get editor value from either editNode (if supplied) or controller function
		 * @param {Context} ctx
		 * @param {DOMNode} [editNode]
		 * @return {string}
		 */
		getEditorValue: function(ctx, editNode) {
			return editNode ? editNode.get("value") : ctx.ac?.getInlineEditValue();
		},

		getQuerySpec: function(/*Context*/ctx, /*Heading*/h, /*Spec*/spec) {
			// summary: if using factor, then disable filtering/sorting as factor may depend on context
			if (this._getFactor(ctx)) {
				return null;
			}

			// the Number renderer class is used for any numeric type, so use spec.type rather than this.type as in base class
			var qs = this.inherited(arguments);
			qs.type = xml.attributeTypeGetBase(spec.type) || spec.type;
			return qs;
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: override because numbers always have value
			refNode.innerHTML = this.toValue(ctx, h);
			return true;
		},

		toHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Object*/params) {
			// summary: render editable	control for attribute - make sure to store conversion factor prior to formatting value
			var unparsedValue = params.value;
			params.value = this.toValueDynamic(ctx, h, params.value);

			// FIX: not quite ready to use NumberTextBox for inline editing, but use on form
			if (ctx.defView.getType() == "Form") {
				var format = h.m_format;

				if (format && format.length && format[0] === '0') {
					format = '#' + format;
				}

				params.constraints = params.constraints || {};
				params.constraints.pattern = params.constraints.pattern || format;

				if (params.editOptions == null) {
					params.editOptions = {pattern: '#.##########'};
				}

				if (format && format.indexOf("%") >= 0) {
					params.value = unparsedValue;
					return new PercentageTextBox(params);
				}

				return new NumberTextBox(params);
			}

			return new ValidationTextBox(params);
		},

		fromHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/editNode, /*boolean*/bFromForm) {
			// on forms, we use the number box and don't need to do this processing.
			if (bFromForm) {
				return editNode.get("value");
			}

			// summary: return attribute value from edit control - convert number based on conversion factor and if fraction
			const editorValue = this.getEditorValue(ctx, editNode);
			var value = editorValue === "" ? "" : types.getInternalNumber(editorValue);

			if (this._isFraction(ctx)) {
				// validate that number entered is supported fraction
				var v = types.getInternalNumber(types.getNumberWrite(value, types.FORMAT_IN_FRACTION));

				if (Math.abs(value - v) >= 0.001) {
					throw new ValidateError(locale.resource("ValidateFraction"));
				}
			}

			if (value === "" || isNaN(value)) {
				return value;
			}

			// if editing a percentage, then convert value to percentage and return - percentages and scaling factors (units) do not mix
			var percentValue = types.getPercentageNumber(value, this._getFormat(ctx, h));
			if (percentValue != null) {
				return percentValue;
			}

			// convert number based on conversion factor (if any)
			value = this.convertValueIfUOM(ctx, h, value, false);

			return bigDiv(value, (bigTimes((this._getFactor(ctx) || 1), this._nFactor)));
		},

		/**
		 * Convert value to correct Unit of Measure if it matches UOM Attribute format
		 * @param {Context} ctx 
		 * @param {Heading} h 
		 * @param {number} value 
		 * @param {boolean} internalToExternal 
		 * @returns number
		 */
		convertValueIfUOM(ctx, h, value, internalToExternal) {
			const node = ctx.getNode();

			if (isValueUOMAttributeOnNode(node, h.m_id)) {
				const conversion = ctx.cache.getNode(node[getUOMConversionString(h.m_id)]);
				return convertUOMValue(conversion, value, internalToExternal);
			}

			return value;
		},

		toValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return formatted value for heading in context
			var fmt = this._getFormat(ctx, h);
			let value = this._getValue(ctx, h);
			value = this._applyFactor(ctx, value);
			return this.formatValue(value, fmt);
		},

		toValueDynamic: function(/*Context*/ctx, /*Heading*/h, /*Number?*/v) {
			// summary: convert value to editable value based on format
			ctx.dynamicNumber = true;

			var fmt = this._getFormat(ctx, h);
			var value = (v == null) ? (this._getValue(ctx, h) || 0) : v;
			value = this._applyFactor(ctx, value);

			ctx.dynamicNumber = false;

			return types.getNumberWrite(bigTimes(value, this._nFactor), fmt);
		},

		/**
		 * Call conversion for UOM Attribute if it matches the format
		 * @override
		 */
		_getValue(ctx, h) {
			const value = this.inherited(arguments);
			return this.convertValueIfUOM(ctx, h, value, true);
		},

		_applyFactor: function(/*Context*/ctx, /*Number*/value) {
			// summary: apply conversion factor to value if there is one that depends on context
			return value ? (bigTimes(value, this._getFactor(ctx) || 1.0)) : value;
		},

		_getCSS: function(/*String*/type, /*String?*/format) {
			// summary: don't need specific classes for every type of number, and don't use
			// format due to potential special characters
			return "attrNumber";
		},

		_getFactor: function(/*Context*/ctx) {
			// summary: return conversion factor if based on context
			return null;
		},

		_getFormat: function(/*Context*/ctx, /*Heading*/h) {
			// summary: if displaying fractions, then use that format; otherwise use configured format
			var fmt = this._isFraction(ctx) ? types.FORMAT_IN_FRACTION : this.inherited(arguments);
			return fmt == null ? types.getDefaultNumberFormat(h.getAttrType()) : fmt;
		},

		_isFraction: function(/*Context*/ctx) {
			// summary: return if value should be represented as fraction
			return false;
		},

		/**
		 * @override
		 */
		getReactPlaceholderOptions: function(controller, ctx, h) {
			const {oldValue} = controller;
			const value = this.toValueDynamic(ctx, h);
			return {
				value,
			};
		},

		/**
		 * @override
		 * @param {context} ctx
		 * @return {string}
		 */
		getReactControlType: function(ctx) {
			return CONTROL_TYPE_TEXT_FIELD;
		},

		/**
		 * return true if react renderer exists for given attribute type and should be used
		 */
		useReactInlineDialog() {
			return true;
		},
	});
});